import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Menu, ChevronDown, LogOut, User, LayoutDashboard } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';

// Helper function to get the correct dashboard route based on user role
const getDashboardRoute = (role) => {
  const dashboardRoutes = {
    admin: '/admin',
    examiner: '/examiner',
    exam_center: '/dashboard/examination-center',
    examination_center: '/dashboard/examination-center',
    institution: '/dashboard/accreditation',
    institutional: '/dashboard/accreditation',
    accreditation: '/dashboard/accreditation',
    curriculum: '/dashboard/curriculum',
    candidate: '/dashboard',
    student: '/dashboard'
  };
  return dashboardRoutes[role] || '/dashboard';
};

const navGroups = [
  {
    label: 'About IMCAC',
    items: [
      { to: '/about', label: 'About IMCAC' },
      { to: '/gasi', label: 'GASI - Global Aesthetic Safety Initiative' },
      { to: '/cpm', label: 'CPM - Clinical Progression Matrix' },
      { to: '/advisory-board', label: 'Advisory Board' },
      { to: '/news-media', label: 'News & Media', section: 'Media' },
      { to: '/events-gallery', label: 'Events Gallery' },
    ]
  },
  {
    label: 'Certifications',
    items: [
      { to: '/ipcf', label: 'Certification Framework (IPCF)' },
      { to: '/faculty/aesthetic-medicine', label: 'CBEAM - Aesthetic Medicine', section: 'Medical Doctors' },
      { to: '/faculty/longevity-medicine', label: 'CBELM - Longevity Medicine' },
      { to: '/faculty/regenerative-medicine', label: 'CBERM - Regenerative Medicine' },
      { to: '/examination/cbe-lmm', label: 'CBE-LMM - Longevity & Metabolic Medicine', section: 'Multi-Specialty' },
      { to: '/dental-faculty/aesthetic-dentistry', label: 'CBEAD - Aesthetic Dentistry', section: 'Dental' },
      { to: '/allied-faculty/aesthetic-nursing', label: 'CBENA - Aesthetic Nursing', section: 'Allied (Nursing & Assistants)' },
      { to: '/allied-faculty/clinical-assistance', label: 'CBEAA - Clinical Assistance' },
    ]
  },
  {
    label: 'Examinations',
    items: [
      { to: '/examination-structure', label: 'Examination Structure' },
      { to: '/examination-pathways', label: 'Examination Pathways' },
      { to: '/osce', label: 'OSCE Structure' },
      { to: '/examination-framework', label: 'Examination Framework' },
      { to: '/candidate-handbook', label: 'Candidate Handbook', section: 'Resources' },
    ]
  },
  {
    label: 'Standards & Policies',
    items: [
      { to: '/governance', label: 'Governance & Regulatory Framework' },
      { to: '/governance-structure', label: 'Governance Structure' },
      { to: '/ethics', label: 'Ethics & Integrity' },
      { to: '/policies', label: 'Policies' },
      { to: '/independence-policy', label: 'Independence & Conflict of Interest Policy' },
    ]
  },
];

const directLinks = [
  { to: '/verification', label: 'Certification Verification' },
  { to: '/contact', label: 'Contact' },
];

const cpdRecognitionGroup = {
  label: 'CPD Recognition',
  items: [
    { to: '/cpd-recognition', label: 'Overview' },
    { to: '/cpd-recognition/apply', label: 'Apply for Recognition' },
    { to: '/cpd-recognition/standards', label: 'Recognition Standards' },
    { to: '/cpd-recognition/recognized-activities', label: 'Recognized Activities' },
    { to: '/cpd-recognition/fees', label: 'Fees & Process' },
    { to: '/cpd-recognition/faq', label: 'Frequently Asked Questions' },
  ]
};

export default function Header() {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200" data-testid="main-header">
      <div className="h-1 bg-[#C5A572]" />
      <div className="imcac-container">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/2eoy3xqe_IMCAC%20%286%29.png"
              alt="IMCAC Logo"
              className="h-12 w-auto object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {[...navGroups, cpdRecognitionGroup].map((group) => (
              <DropdownMenu key={group.label}>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 px-2.5 py-2 text-sm font-medium text-slate-700 hover:text-[#1B2A4A] transition-colors rounded-sm" data-testid={`nav-${group.label.toLowerCase()}`}>
                    {group.label}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  {group.items.map((item, idx) => (
                    <div key={item.to}>
                      {item.section && idx > 0 && <DropdownMenuSeparator />}
                      {item.section && (
                        <p className="px-2 py-1.5 text-xs font-semibold text-[#C5A572] uppercase tracking-wider">{item.section}</p>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to={item.to} className="w-full cursor-pointer" data-testid={`nav-link-${item.to.slice(1).replace(/\//g, '-')}`}>
                          {item.label}
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            {directLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`px-2.5 py-2 text-sm font-medium transition-colors rounded-sm ${
                  location.pathname === link.to ? 'text-[#1B2A4A] font-semibold' : 'text-slate-700 hover:text-[#1B2A4A]'
                }`}
                data-testid={`nav-link-${link.to.slice(1)}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="hidden md:flex items-center gap-2 rounded-sm border-[#1B2A4A] text-[#1B2A4A]" data-testid="user-menu-trigger">
                    <User className="h-4 w-4" />
                    <span className="max-w-[120px] truncate">{user?.full_name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardRoute(user?.role)} className="cursor-pointer" data-testid="nav-dashboard">
                      <LayoutDashboard className="h-4 w-4 mr-2" /> Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {user?.role === 'admin' && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="cursor-pointer" data-testid="nav-admin">
                        <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={logout} className="cursor-pointer" data-testid="logout-btn">
                    <LogOut className="h-4 w-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <NotificationBell />
              </>
            ) : (
              <>
                <Link to="/verification" className="hidden md:block" data-testid="nav-explore-btn">
                  <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm text-sm px-4">
                    Verify Credentials
                  </Button>
                </Link>
                <Link to="/login" className="hidden md:block" data-testid="nav-login-btn">
                  <Button variant="outline" className="rounded-sm text-sm border-[#1B2A4A] text-[#1B2A4A]">
                    Sign In
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="hidden md:flex items-center gap-1 text-sm text-[#1B2A4A]">
                      All Portals <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem asChild>
                      <Link to="/login" className="flex items-center gap-2">
                        <User className="h-4 w-4" /> Candidate Portal
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/login/examiner" className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4" /> Examiner Portal
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/login/institutional" className="flex items-center gap-2">
                        Institution Accreditation
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/login/curriculum" className="flex items-center gap-2">
                        Curriculum Submission
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/login/examination-center" className="flex items-center gap-2">
                        Examination Center
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/portals" className="text-[#C5A572] font-medium">
                        View All Portals →
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 overflow-y-auto">
                <div className="flex flex-col gap-2 mt-8">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="py-2 px-3 text-sm font-medium text-[#1B2A4A] hover:bg-slate-50 rounded-sm">Home</Link>
                  {[...navGroups, cpdRecognitionGroup].map((group) => (
                    <div key={group.label} className="mb-2">
                      <p className="px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#C5A572]">{group.label}</p>
                      {group.items.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          onClick={() => setMobileOpen(false)}
                          className="block py-2 px-3 pl-6 text-sm text-slate-700 hover:text-[#1B2A4A] hover:bg-slate-50 rounded-sm"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  ))}
                  {directLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setMobileOpen(false)}
                      className="py-2 px-3 text-sm font-medium text-slate-700 hover:text-[#1B2A4A] hover:bg-slate-50 rounded-sm"
                    >
                      {link.label}
                    </Link>
                  ))}
                  <div className="border-t border-slate-200 mt-4 pt-4 space-y-2">
                    {isAuthenticated ? (
                      <>
                        <Link to={getDashboardRoute(user?.role)} onClick={() => setMobileOpen(false)}>
                          <Button className="w-full bg-[#1B2A4A] text-white rounded-sm">Dashboard</Button>
                        </Link>
                        <Button variant="outline" className="w-full rounded-sm" onClick={() => { logout(); setMobileOpen(false); }}>Sign Out</Button>
                      </>
                    ) : (
                      <>
                        <Link to="/verification" onClick={() => setMobileOpen(false)}>
                          <Button className="w-full bg-[#1B2A4A] text-white rounded-sm">Verify Credentials</Button>
                        </Link>
                        <Link to="/login" onClick={() => setMobileOpen(false)}>
                          <Button variant="outline" className="w-full rounded-sm border-[#1B2A4A] text-[#1B2A4A]">Sign In</Button>
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
